import React, { useState, useRef, useEffect, useCallback } from "react";
import { RiMenu3Line, RiCloseLine } from 'react-icons/ri';
import { Link, useNavigate, useLocation } from "react-router-dom";
import './navbar.css';
import { LuHome, LuPlus, LuUser, LuMessageSquare, LuShield, LuSettings, LuHelpCircle, LuFileText, LuMail, LuLogOut, LuLogIn } from 'react-icons/lu';
import { IoCarSportOutline } from 'react-icons/io5';
import axios from "axios";
import NotificationBell from "../notifications/NotificationBell";

const Navbar = ( {isLoggedIn, loginUser, onLogout } ) => {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [hasNewCommunity, setHasNewCommunity] = useState(false);
  const [hasNewFeed, setHasNewFeed] = useState(false);
  const notifRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkCommunityActivity = useCallback(async () => {
    try {
      const res = await axios.get("/api/v1/discussions/latest-activity");
      const latest = res.data.lastActivityAt;
      if (!latest) {
        setHasNewCommunity(false);
        return;
      }
      const lastSeen = localStorage.getItem("lastSeenCommunity");
      setHasNewCommunity(!lastSeen || new Date(latest) > new Date(lastSeen));
    } catch {
      // silently ignore
    }
  }, []);

  const checkFeedActivity = useCallback(async () => {
    try {
      const res = await axios.get("/api/v1/ride/latest-activity");
      const latest = res.data.lastActivityAt;
      if (!latest) {
        setHasNewFeed(false);
        return;
      }
      const lastSeen = localStorage.getItem("lastSeenFeed");
      setHasNewFeed(!lastSeen || new Date(latest) > new Date(lastSeen));
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    checkCommunityActivity();
    checkFeedActivity();
    const interval = setInterval(() => {
      checkCommunityActivity();
      checkFeedActivity();
    }, 60000);
    return () => clearInterval(interval);
  }, [isLoggedIn, checkCommunityActivity, checkFeedActivity]);

  useEffect(() => {
    if (location.pathname === "/community") {
      setHasNewCommunity(false);
    }
    if (location.pathname === "/") {
      setHasNewFeed(false);
    }
  }, [location.pathname]);

  const onButtonClick = async () => {
    try {
      await axios.post('api/v1/logout');
      onLogout();
      navigate('/login');
    } catch (error) {
      if (error.response) {
        //console.log(error.response.data.message);
      } else {
        //console.log('An error occurred on Logout. Please try again.');
      }
    }
  };

  return (
    <>
      <div className="sr__topbar">
        <button className="sr__topbar-hamburger" onClick={() => { notifRef.current?.close(); setToggleMenu(true); }} aria-label="Open menu">
          <RiMenu3Line size={18} />
        </button>
        <div className="sr__topbar-right">
          {isLoggedIn && <NotificationBell ref={notifRef} />}
        </div>
      </div>

      {/* Overlay */}
      {toggleMenu && <div className="sr__drawer-overlay" onClick={() => setToggleMenu(false)} />}

      {/* Drawer */}
      <div className={`sr__drawer ${toggleMenu ? 'sr__drawer--open' : ''}`}>
        <div className="sr__drawer-header">
          {isLoggedIn && loginUser ? (
            <Link to="/profile" className="sr__drawer-profile" onClick={() => setToggleMenu(false)}>
              <img src={loginUser.avatar} alt="" className="sr__drawer-avatar" />
              <div className="sr__drawer-profile-info">
                <span className="sr__drawer-profile-name">{loginUser.userName}</span>
                <span className="sr__drawer-profile-email">{loginUser.email}</span>
              </div>
            </Link>
          ) : (
            <span className="sr__drawer-title">SastiRide</span>
          )}
          <button className="sr__drawer-close" onClick={() => setToggleMenu(false)} aria-label="Close menu">
            <RiCloseLine size={22} />
          </button>
        </div>

        <nav className="sr__drawer-links" onClick={() => setToggleMenu(false)}>
          {isLoggedIn && loginUser?.role === "admin" && (
            <Link to="/admin" className="sr__drawer-link">
              <LuShield />
              <span>Admin Panel</span>
            </Link>
          )}
          {isLoggedIn && !loginUser.googleId && (
            <Link to="/change-password" className="sr__drawer-link">
              <LuSettings />
              <span>Settings</span>
            </Link>
          )}
          <Link to="/faqs" className="sr__drawer-link">
            <LuHelpCircle />
            <span>FAQs</span>
          </Link>
          <Link to="/terms" className="sr__drawer-link">
            <LuFileText />
            <span>Terms of Service</span>
          </Link>
          <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL}`} className="sr__drawer-link">
            <LuMail />
            <span>Contact Us</span>
          </a>
        </nav>

        <div className="sr__drawer-footer" onClick={() => setToggleMenu(false)}>
          {isLoggedIn ? (
            <button type="button" className="sr__drawer-footer-btn sr__drawer-footer-btn--logout" onClick={onButtonClick}>
              <LuLogOut />
              Log out
            </button>
          ) : (
            <Link to="/login" className="sr__drawer-footer-btn sr__drawer-footer-btn--signin">
              <LuLogIn />
              Sign in
            </Link>
          )}
        </div>
      </div>

      {/* Bottom mobile nav */}
      {isLoggedIn && (
        <nav className="sr__mobile-nav">
          <Link to="/" className={`sr__mobile-nav-item ${location.pathname === '/' ? 'sr__mobile-nav-item--active' : ''}`} aria-label="Feed">
            <LuHome />
            {hasNewFeed && location.pathname !== "/" && <span className="sr__mobile-nav-badge" />}
          </Link>
          <Link to="/community" className={`sr__mobile-nav-item ${location.pathname === '/community' ? 'sr__mobile-nav-item--active' : ''}`} aria-label="Community">
            <LuMessageSquare />
            {hasNewCommunity && location.pathname !== "/community" && <span className="sr__mobile-nav-badge" />}
          </Link>
          <Link to="/publish" className={`sr__mobile-nav-item ${location.pathname === '/publish' ? 'sr__mobile-nav-item--active' : ''}`} aria-label="Publish">
            <span className="sr__mobile-nav-publish-btn"><LuPlus /></span>
          </Link>
          <Link to="/listing" className={`sr__mobile-nav-item ${location.pathname === '/listing' ? 'sr__mobile-nav-item--active' : ''}`} aria-label="My Rides">
            <IoCarSportOutline />
          </Link>
          <Link to="/profile" className={`sr__mobile-nav-item ${location.pathname.startsWith('/profile') ? 'sr__mobile-nav-item--active' : ''}`} aria-label="Profile">
            <LuUser />
          </Link>
        </nav>
      )}
    </>
  );
}

export default Navbar;
